<template>
  <div class="wrap">
    <div class="card">
      <div class="brand">
        <img class="brandLogo" :src="ravenLogo" alt="Raven" />
        <div class="h">Raven GTM</div>
      </div>

      <template v-if="error">
        <div class="status-icon error-icon">⚠️</div>
        <div class="status-text error">Authentication Error</div>
        <div class="error-message">{{ error }}</div>
        <button class="btn primary" @click="retryLogin">
          Try Again
        </button>
      </template>

      <template v-else>
        <div class="status-icon">🔄</div>
        <div class="status-text">Completing authentication...</div>
        <div class="sub-text">Please wait while we verify your session.</div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getAccessToken, getAuthMode } from '../services/auth'
import { loadCurrentUser } from '../services/user.ts'
import { getBuiltAccessMode } from '../routeTable.ts'
import { hardNavigate } from '../services/navigation.ts'
import ravenLogo from '../assets/raven.svg'

const router = useRouter()
const error = ref('')

onMounted(async () => {
  const authMode = getAuthMode()

  if (authMode !== 'external') {
    // Not in external auth mode, redirect to home
    router.push('/home')
    return
  }

  try {
    // Wait a moment for cookie to be fully set by browser
    await new Promise(resolve => setTimeout(resolve, 100))

    // Verify the session cookie is present
    const token = getAccessToken()
    if (!token) {
      throw new Error('Authentication session not found. The cookie may not have been set properly.')
    }

    // Validate the session with the backend (loadCurrentUser = same /auth/me
    // request, plus the dev access-mode override and the in-memory cache the
    // boot fetch shares).
    const me = await loadCurrentUser()
    if (!me) {
      throw new Error('Failed to verify your session. Please try again.')
    }

    // Get the intended destination from query params
    const urlParams = new URLSearchParams(window.location.search)
    const returnPath = urlParams.get('returnPath') || '/home'

    // The route table was chosen at boot, before this login: if the resolved
    // access mode implies a different table, one full reload rebuilds it with
    // the token now present. Full users take the SPA push, byte-identical.
    const resolvedMode = me?.access_mode === 'dialer_only' ? 'dialer_only' : 'full'
    if (resolvedMode !== getBuiltAccessMode()) {
      hardNavigate(returnPath)
      return
    }

    // Successful authentication, redirect to intended page
    router.push(returnPath)
  } catch (e) {
    console.error('Callback authentication error:', e)
    error.value = e?.message || 'Failed to complete authentication. Please try again.'
  }
})

function retryLogin() {
  // Clear any partial auth state and try again
  const authMode = getAuthMode()
  if (authMode === 'external') {
    const externalLoginUrl = import.meta.env.VITE_EXTERNAL_IDP_LOGIN_URL
    if (externalLoginUrl) {
      const returnPath = new URLSearchParams(window.location.search).get('returnPath') || '/home'
      const loginUrl = buildLoginUrl(externalLoginUrl, returnPath)
      window.location.href = loginUrl
    } else {
      router.push('/login')
    }
  } else {
    router.push('/login')
  }
}

function buildLoginUrl(baseUrl, returnPath) {
  const callbackUrl = `${window.location.origin}/callback?returnPath=${encodeURIComponent(returnPath)}`
  return `${baseUrl}?appPath=${encodeURIComponent(callbackUrl)}`
}
</script>

<style scoped>
.wrap {
  height: 100vh;
  display: grid;
  place-items: center;
  background: #f9fafb;
}

.card {
  width: min(420px, 92vw);
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.brandLogo {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.h {
  font-weight: 700;
  font-size: 18px;
}

.status-icon {
  font-size: 48px;
  margin: 16px 0;
}

.error-icon {
  filter: grayscale(1);
}

.status-text {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.status-text.error {
  color: var(--danger);
}

.sub-text {
  font-size: 13px;
  color: var(--muted);
  margin-top: -8px;
}

.error-message {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  padding: 12px;
  background: #fef2f2;
  border-radius: 6px;
  border: 1px solid #fecaca;
}

.btn {
  margin-top: 8px;
  width: 100%;
}
</style>
